import React, { useEffect, useState } from "react";
import api, { fmt, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import { Plus, Trash, X } from "@phosphor-icons/react";

const METHODS = [
  ["sold", "Sold"],
  ["scrapped", "Scrapped"],
  ["donated", "Donated"],
  ["lost", "Lost"],
  ["other", "Other"],
];

export default function AssetRetirementPage() {
  const [rows, setRows] = useState([]);
  const [assets, setAssets] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    asset_id: "", retire_date: new Date().toISOString().slice(0, 10),
    reason: "", salvage_value: "0", disposal_method: "scrapped", notes: "",
  });

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/alm/retirements"), api.get("/alm/assets")]);
    setRows(a.data); setAssets(b.data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    await api.post("/alm/retirements", {
      ...form,
      salvage_value: parseFloat(form.salvage_value || 0),
    });
    setOpen(false);
    setForm({ ...form, asset_id: "", reason: "", salvage_value: "0", notes: "" });
    load();
  };

  const remove = async (id) => { if (window.confirm("Delete this retirement?")) { await api.delete(`/alm/retirements/${id}`); load(); } };

  return (
    <div>
      <PageHeader
        eyebrow="Asset Lifecycle"
        title="Asset Retirement"
        subtitle="Retire assets, record salvage value and compute gain/loss vs book cost."
        testid="alm-ret-title"
        actions={
          <button onClick={() => setOpen(true)} data-testid="alm-ret-new-btn" className="bg-[#0020C2] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#001AA3] flex items-center gap-2">
            <Plus size={14} weight="bold" /> Retire Asset
          </button>
        }
      />
      <div className="p-8">
        <div className="border border-gray-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="alm-ret-table">
            <thead className="bg-gray-50">
              <tr className="text-left">
                {["Asset", "Serial", "Retire Date", "Method", "Reason", "Book Cost", "Salvage", "Gain/Loss", ""].map(h => (
                  <th key={h} className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs font-bold">{r.asset_tag}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.serial_number || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{fmtDate(r.retire_date)}</td>
                  <td className="px-4 py-3 text-xs uppercase">{r.disposal_method}</td>
                  <td className="px-4 py-3 text-xs">{r.reason}</td>
                  <td className="px-4 py-3 font-mono text-right">{fmt(r.asset_cost)}</td>
                  <td className="px-4 py-3 font-mono text-right">{fmt(r.salvage_value)}</td>
                  <td className={`px-4 py-3 font-mono text-right font-bold ${r.gain_loss >= 0 ? "text-green-700" : "text-[#DC2626]"}`}>{fmt(r.gain_loss)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => remove(r.id)} data-testid={`alm-ret-del-${r.id}`} className="text-[#DC2626] hover:underline text-xs font-bold">
                      <Trash size={12} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={9} className="px-4 py-12 text-center text-sm text-gray-500">No retirements yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end" onClick={() => setOpen(false)}>
          <form onClick={e => e.stopPropagation()} onSubmit={create} className="w-full max-w-md bg-white h-full overflow-y-auto" data-testid="alm-ret-form">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Retire</div>
                <h2 className="text-2xl font-black tracking-tight">Asset Retirement</h2>
              </div>
              <button type="button" onClick={() => setOpen(false)}><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Asset *</label>
                <select required value={form.asset_id} onChange={e => setForm({ ...form, asset_id: e.target.value })} data-testid="alm-ret-asset" className="w-full border border-gray-300 px-3 py-2.5 text-sm">
                  <option value="">— Select Asset —</option>
                  {assets.filter(a => a.status !== "retired").map(a => <option key={a.id} value={a.id}>{a.asset_tag} — {a.product_code} ({fmt(a.cost)})</option>)}
                </select>
              </div>
              {[
                ["retire_date", "Retire Date *", "date", true],
                ["reason", "Reason", "text", false],
                ["salvage_value", "Salvage Value", "number", false],
                ["notes", "Notes", "text", false],
              ].map(([k, label, type, required]) => (
                <div key={k}>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">{label}</label>
                  <input type={type} step="any" required={required} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} data-testid={`alm-ret-${k}`} className="w-full border border-gray-300 px-3 py-2.5 text-sm" />
                </div>
              ))}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Disposal Method</label>
                <select value={form.disposal_method} onChange={e => setForm({ ...form, disposal_method: e.target.value })} data-testid="alm-ret-method" className="w-full border border-gray-300 px-3 py-2.5 text-sm">
                  {METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200">
              <button type="submit" data-testid="alm-ret-submit" className="w-full bg-[#0020C2] text-white py-3 text-sm font-bold uppercase tracking-wider hover:bg-[#001AA3]">Retire Asset</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
